/**
 * Configuration for routes that should have scrollable content
 *
 * This allows centralized control over which pages/routes should allow
 * content overflow and scrolling vs. using overflow-hidden layouts.
 */

/**
 * List of route patterns that should allow content to scroll
 *
 * Supports:
 * - Exact matches: '/ui/my-workforce'
 * - Path patterns: '/ui/reports/*' (matches any route starting with /ui/reports/)
 * - Wildcards: Use '*' for dynamic segments
 */
export const SCROLLABLE_ROUTES = [
  // Main pages
  // NOTE: '/ui/skills' is intentionally absent. Like the Knowledge Hub, the
  // Skills page is a two-pane layout (fixed left pane + routed right viewer)
  // that manages its own internal scroll, so the layout must clip rather than
  // scroll the page.
  '/ui/glossary',
  '/ui/routines',
  '/ui/routines/*', // Matches /ui/routines/:routineId
  '/ui/my-workforce',
  '/ui/marketplace',
  '/ui/experiments',
  // NOTE: '/ui/settings' is intentionally absent. Like Skills and the Knowledge
  // Hub, Settings is a two-pane layout whose right pane owns its own scroll, so
  // the shell must clip rather than add a second scroll container.
  '/ui/onboarding/setup',

  '/ui/reports',
  // Dynamic routes (use pattern matching)
  '/ui/reports/*', // Matches /ui/reports/:insightId

  // Demo/utility pages
  '/ui/charts',
  '/ui/slots',
  '/ui/insights',
  '/ui/block-kit-playground',

  // Demo agents (all routes between the demo comments in App.tsx)
  '/ui/agent/bant-qualifier-avoma',
  '/ui/agent/outbound-call-ai-agent',
  '/ui/agent/keyword-optimizer',
  '/ui/agent/lead-funnel-analyser-demo',
  '/ui/agent/linkedin-campaign-analyser-demo',
  '/ui/agent/google-ads-campaign-analyser-demo',
  '/ui/agent/recki',
  '/ui/agent/blog-post-refresh-hevo',
  '/ui/agent/content-repurposing',
  '/ui/agent/answer-engine-optimization-demo',

  // Non-screen agents
  '/ui/agent/aeo',
  '/ui/agent/seo-audit',
  '/ui/agent/seo-audit/*',
  '/ui/agent/blog-post-content-strategizer',
  '/ui/agent/blog-post-content-strategizer/*',
  '/ui/agent/blog-post-content-refresher',
  '/ui/agent/blog-post-content-refresher/*',
  '/ui/agent/negative-keyword-analyzer',
  '/ui/agent/negative-keyword-analyzer/*',
] as const;

/**
 * Check if a given route path should allow content overflow/scrolling
 *
 * @param pathname - The current route pathname (e.g., from useLocation())
 * @returns true if the route should be scrollable, false otherwise
 *
 * @example
 * ```ts
 * const location = useLocation();
 * const isScrollable = isScrollableRoute(location.pathname);
 * ```
 */
export function isScrollableRoute(pathname: string): boolean {
  return SCROLLABLE_ROUTES.some(route => {
    // Exact match
    if (route === pathname) {
      return true;
    }

    // Pattern match (e.g., '/ui/reports/*' matches '/ui/reports/123')
    if (route.endsWith('/*')) {
      const baseRoute = route.slice(0, -1); // e.g. '/ui/reports/*' -> '/ui/reports/'
      return pathname.startsWith(baseRoute);
    }

    return false;
  });
}

